// src/components/HomePage.js

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Terminal,
  AccessTime,
  Edit as EditIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiFetch from '../utils/api';

function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  // Logs modal state
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsBot, setLogsBot] = useState(null);
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logLines, setLogLines] = useState(100);
  const logsEndRef = useRef(null);

  useEffect(() => {
    fetchBots();
  }, []);

  useEffect(() => {
    if (logs && logsEndRef.current) {
      logsEndRef.current.scrollTop = logsEndRef.current.scrollHeight;
    }
  }, [logs]);

  const fetchBots = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/bots', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const all = [
          ...(data.owned || []).map((b) => ({ ...b, ownership: 'owner' })),
          ...(data.managed || []).map((b) => ({ ...b, ownership: 'managed' })),
        ];
        setBots(all);
      } else {
        throw new Error('Failed to fetch bots');
      }
    } catch (err) {
      console.error('Error fetching bots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePowerAction = async (bot, action) => {
    const key = `${bot.namespace}-${bot.name}`;
    setActionLoading((prev) => ({ ...prev, [key]: action }));
    try {
      const response = await apiFetch(`/api/bots/${bot.namespace}/${bot.name}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        await fetchBots();
      } else {
        throw new Error(`Failed to ${action} bot`);
      }
    } catch (err) {
      console.error(`Error ${action}ing bot:`, err);
      setError(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [key]: null }));
    }
  };

  const openLogs = (bot) => {
    setLogsBot(bot);
    setLogsOpen(true);
    setLogs('');
    setLogsError(null);
    fetchLogs(bot, logLines);
  };

  const fetchLogs = async (bot, lines) => {
    const target = bot || logsBot;
    if (!target) return;
    setLogsLoading(true);
    setLogsError(null);
    try {
      const response = await apiFetch(
        `/api/bots/${target.namespace}/${target.name}/logs?lines=${lines || logLines}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || '');
      } else {
        throw new Error('Failed to fetch logs');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLogLinesChange = (e) => {
    const value = e.target.value;
    setLogLines(value);
    fetchLogs(logsBot, value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success';
      case 'stopped': return 'default';
      case 'pending': case 'creating': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case 'running': return '#66bb6a';
      case 'stopped': return '#9e9e9e';
      case 'pending': case 'creating': return '#42a5f5';
      case 'error': return '#ef5350';
      default: return '#9e9e9e';
    }
  };

  const getUptime = (bot) => {
    if (bot.status !== 'running') return null;
    const startedAt = bot.startedAt || bot.started_at || bot.pod_started_at;
    if (!startedAt) return null;
    const start = new Date(startedAt);
    if (isNaN(start.getTime())) return null;
    const now = new Date();
    const diffMs = now - start;
    if (diffMs < 0) return null;
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return '< 1m';
  };

  const canOperate = (bot) => {
    return bot.ownership === 'owner' || bot.role === 'operator' || bot.role === 'manager';
  };

  const runningCount = bots.filter((b) => b.status === 'running').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
              Welcome back{user?.username ? `, ${user.username}` : ''}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {bots.length === 0
                ? 'Get started by creating your first bot.'
                : `${runningCount} of ${bots.length} bot${bots.length !== 1 ? 's' : ''} running`}
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchBots} sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {bots.length === 0 ? (
        <Card sx={{ border: '1px dashed rgba(255,255,255,0.12)' }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <BotIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.4 }} />
            <Typography variant="h6" gutterBottom>
              No bots found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Create your first modmail bot to get started.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/bots')}>
              Go to My Bots
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {bots.map((bot) => {
            const key = `${bot.namespace}-${bot.name}`;
            const currentAction = actionLoading[key];
            const uptime = getUptime(bot);

            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    {/* Header: name + status dot */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" sx={{ lineHeight: 1.3, fontSize: '1.05rem' }} noWrap>
                          {bot.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {bot.namespace}
                        </Typography>
                      </Box>
                      <Chip
                        icon={<DotIcon sx={{ fontSize: '10px !important', color: `${getStatusDotColor(bot.status)} !important` }} />}
                        label={bot.status}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'rgba(255,255,255,0.1)',
                          fontSize: '0.75rem',
                          height: 26,
                          ml: 1,
                        }}
                      />
                    </Box>

                    {/* Uptime */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                      <AccessTime sx={{ fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {uptime ? `Uptime: ${uptime}` : bot.status === 'running' ? 'Just started' : 'Offline'}
                      </Typography>
                    </Box>

                    {bot.restart_count > 0 && (
                      <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                        {bot.restart_count} restart{bot.restart_count !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </CardContent>

                  {/* Actions */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      {canOperate(bot) && (
                        <>
                          {bot.status === 'stopped' || bot.status === 'creating' || bot.status === 'pending' ? (
                            <Tooltip title="Start">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handlePowerAction(bot, 'start')}
                                disabled={!!currentAction}
                              >
                                {currentAction === 'start' ? <CircularProgress size={18} /> : <StartIcon />}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Stop">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handlePowerAction(bot, 'stop')}
                                disabled={!!currentAction}
                              >
                                {currentAction === 'stop' ? <CircularProgress size={18} /> : <StopIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Restart">
                            <IconButton
                              size="small"
                              onClick={() => handlePowerAction(bot, 'restart')}
                              disabled={!!currentAction}
                            >
                              {currentAction === 'restart' ? <CircularProgress size={18} /> : <RefreshIcon />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                    <Box>
                      <Tooltip title="View Logs">
                        <IconButton size="small" onClick={() => openLogs(bot)}>
                          <Terminal sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Configure">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/bots/${bot.namespace}/${bot.name}`)}
                        >
                          <EditIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Logs Modal */}
      <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Terminal />
            <span>Logs: {logsBot?.name}</span>
          </Box>
          <IconButton size="small" onClick={() => setLogsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Lines</InputLabel>
              <Select value={logLines} label="Lines" onChange={handleLogLinesChange}>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={250}>250</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => fetchLogs(logsBot)}
              disabled={logsLoading}
            >
              Refresh
            </Button>
            {logsLoading && <CircularProgress size={20} />}
          </Box>
          {logsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {logsError}
            </Alert>
          )}
          <Box
            ref={logsEndRef}
            component="pre"
            sx={{
              bgcolor: '#0d1117',
              color: '#7ee787',
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '12px',
              p: 2,
              borderRadius: 1,
              maxHeight: 400,
              minHeight: 200,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              m: 0,
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {logs || (logsLoading ? '' : 'No logs available.')}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HomePage;
